import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import { toast } from "react-toastify";
import usePwaInstall from "../../hooks/usePwaInstall";
import { requestWebPushPermission } from "../../services/webPushNotifications";
import { i18n } from "../../translate/i18n";

const INSTALL_DISMISS_KEY = "watoolx-pwa-install-dismissed";
const NOTIFY_DISMISS_KEY = "watoolx-pwa-notify-dismissed";

const useStyles = makeStyles((theme) => ({
  bar: {
    position: "fixed",
    left: 8,
    right: 8,
    bottom: "calc(8px + env(safe-area-inset-bottom, 0px))",
    zIndex: 1400,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 10px 10px 12px",
    borderRadius: 12,
    backgroundColor: theme.mode === "dark" ? "#111827" : "#0f172a",
    color: "#fff",
    boxShadow: "0 12px 28px rgba(15,23,42,0.28)",
  },
  copy: {
    minWidth: 0,
    flex: 1,
  },
  title: {
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.25,
  },
  body: {
    display: "block",
    fontSize: 11,
    opacity: 0.82,
    lineHeight: 1.35,
  },
  action: {
    flexShrink: 0,
    minHeight: 36,
    whiteSpace: "nowrap",
    backgroundColor: "#fff",
    color: "#0f172a",
    fontWeight: 700,
    "&:hover": {
      backgroundColor: "#e2e8f0",
    },
  },
  close: {
    color: "rgba(255,255,255,0.78)",
    padding: 4,
  },
}));

export const notificationPermission = () =>
  typeof Notification !== "undefined" ? Notification.permission : "denied";

export const IosInstallDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>{i18n.t("mainDrawer.appBar.pwa.iosTitle")}</DialogTitle>
    <DialogContent dividers>
      <Typography style={{ marginBottom: 8 }}>
        {i18n.t("mainDrawer.appBar.pwa.iosBody")}
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary">
        {i18n.t("mainDrawer.appBar.pwa.gotIt")}
      </Button>
    </DialogActions>
  </Dialog>
);

export const enableAppNotifications = async () => {
  const result = await requestWebPushPermission();
  if (result.ok) {
    toast.success(i18n.t("mainDrawer.appBar.pwa.notificationsOn"));
    localStorage.setItem(NOTIFY_DISMISS_KEY, "1");
  } else if (result.reason === "denied") {
    toast.warn(i18n.t("mainDrawer.appBar.pwa.permissionDenied"));
  }
  window.dispatchEvent(new Event("watoolx-push-updated"));
  return result;
};

const PwaInstallBanner = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { canInstall, promptInstall, isStandalone, isIos } = usePwaInstall();
  const [installDismissed, setInstallDismissed] = useState(
    () => localStorage.getItem(INSTALL_DISMISS_KEY) === "1"
  );
  const [notifyDismissed, setNotifyDismissed] = useState(
    () => localStorage.getItem(NOTIFY_DISMISS_KEY) === "1"
  );
  const [iosOpen, setIosOpen] = useState(false);
  const [permission, setPermission] = useState(notificationPermission);

  useEffect(() => {
    const sync = () => {
      setPermission(notificationPermission());
      setNotifyDismissed(localStorage.getItem(NOTIFY_DISMISS_KEY) === "1");
    };
    window.addEventListener("watoolx-push-updated", sync);
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      window.removeEventListener("watoolx-push-updated", sync);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, []);

  const mode = useMemo(() => {
    if (!installDismissed && !isStandalone && (canInstall || isIos)) {
      return "install";
    }
    if (!notifyDismissed && isStandalone && permission === "default") {
      return "notify";
    }
    return null;
  }, [canInstall, installDismissed, isIos, isStandalone, notifyDismissed, permission]);

  const showBanner = Boolean(mode) && (isMobile || (isStandalone && mode === "notify"));
  if (!showBanner) {
    return <IosInstallDialog open={iosOpen} onClose={() => setIosOpen(false)} />;
  }

  const dismiss = () => {
    if (mode === "install") {
      localStorage.setItem(INSTALL_DISMISS_KEY, "1");
      setInstallDismissed(true);
      return;
    }
    localStorage.setItem(NOTIFY_DISMISS_KEY, "1");
    setNotifyDismissed(true);
  };

  const onPrimary = async () => {
    if (mode === "notify") {
      const result = await enableAppNotifications();
      setPermission(notificationPermission());
      if (result.ok) setNotifyDismissed(true);
      return;
    }
    if (canInstall) {
      const choice = await promptInstall();
      if (choice?.outcome === "accepted") {
        const result = await enableAppNotifications();
        setPermission(notificationPermission());
        if (result.ok) setNotifyDismissed(true);
      }
      return;
    }
    setIosOpen(true);
  };

  return (
    <>
      <Paper className={classes.bar} elevation={8}>
        {mode === "install" ? (
          <GetAppIcon fontSize="small" />
        ) : (
          <NotificationsActiveIcon fontSize="small" />
        )}
        <Box className={classes.copy}>
          <Typography className={classes.title}>
            {mode === "install"
              ? i18n.t("mainDrawer.appBar.pwa.bannerInstallTitle")
              : i18n.t("mainDrawer.appBar.pwa.bannerNotifyTitle")}
          </Typography>
          <Typography className={classes.body} variant="caption">
            {mode === "install"
              ? i18n.t("mainDrawer.appBar.pwa.bannerInstallBody")
              : i18n.t("mainDrawer.appBar.pwa.bannerNotifyBody")}
          </Typography>
        </Box>
        <Button className={classes.action} size="small" onClick={onPrimary}>
          {mode === "install"
            ? i18n.t("mainDrawer.appBar.pwa.bannerInstall")
            : i18n.t("mainDrawer.appBar.pwa.bannerEnable")}
        </Button>
        <IconButton className={classes.close} size="small" onClick={dismiss} aria-label="cerrar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
      <IosInstallDialog open={iosOpen} onClose={() => setIosOpen(false)} />
    </>
  );
};

export default PwaInstallBanner;
